import { TipoCampo, type CampoChecklist, type Resposta, type ValorResposta } from './tipos.js'

/// Um problema encontrado na validação. `campoId` vazio significa erro da inspeção
/// como um todo, não de um campo.
export interface Problema {
  campoId: string
  campoNome: string
  mensagem: string
}

const vazio = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '')

/// Uma resposta está preenchida? Depende do tipo — um "Sim/Não" respondido com `false`
/// está preenchido, um texto com espaços em branco não está.
export function respostaPreenchida(campo: CampoChecklist, valor: ValorResposta | undefined): boolean {
  if (!valor) return false
  switch (campo.tipoCampo) {
    case TipoCampo.TEXTO:
      return !vazio(valor.texto) || !vazio(valor.textoLongo)
    case TipoCampo.NUMERO:
      return valor.numero !== null && valor.numero !== undefined && Number.isFinite(valor.numero)
    case TipoCampo.DATA:
      return !vazio(valor.data)
    case TipoCampo.SIM_NAO:
      return valor.booleano === true || valor.booleano === false
    case TipoCampo.SELECAO_UNICA:
      return !vazio(valor.texto)
    case TipoCampo.MULTISSELECAO:
      return (valor.opcoes?.length ?? 0) > 0
    case TipoCampo.IMAGEM:
    case TipoCampo.ASSINATURA:
      return !vazio(valor.arquivoId) || !vazio(valor.texto)
  }
}

/// A resposta de não conformidade: "NC" nas perguntas C / NC / NA do catálogo.
/// Só vale para campo que oferece "NC" — um texto livre com essas letras não é
/// uma não conformidade.
export const NAO_CONFORME = 'NC'

export function ehNaoConformidade(
  campo: Pick<CampoChecklist, 'opcoes'>,
  valor: ValorResposta | undefined,
): boolean {
  if (!valor || !campo.opcoes.includes(NAO_CONFORME)) return false
  return valor.texto === NAO_CONFORME || (valor.opcoes ?? []).includes(NAO_CONFORME)
}

/// O que falta na tratativa de uma não conformidade, na ordem da tela.
export function faltasDaNaoConformidade(valor: ValorResposta): string[] {
  const faltas: string[] = []
  if (vazio(valor.ncResponsavel)) faltas.push('o responsável')
  if (vazio(valor.ncAcao)) faltas.push('a ação a ser tomada')
  if (vazio(valor.ncPrazo)) faltas.push('o prazo')
  return faltas
}

/// O valor de uma resposta gravada, para validar ou desenhar.
export function valorDaResposta(r: Resposta): ValorResposta {
  return {
    texto: r.texto, textoLongo: r.textoLongo, numero: r.numero, data: r.data,
    booleano: r.booleano, opcoes: r.opcoes ?? [], arquivoId: r.arquivoId,
    ncResponsavel: r.ncResponsavel, ncResponsavelId: r.ncResponsavelId, ncAcao: r.ncAcao, ncPrazo: r.ncPrazo,
  }
}

/// Valida uma resposta contra a definição do campo. Roda igual no dispositivo (antes
/// de gravar offline) e no servidor (ao receber a sincronização) — é a razão de este
/// pacote existir.
export function validarResposta(
  campo: CampoChecklist,
  valor: ValorResposta | undefined,
): Problema[] {
  const problemas: Problema[] = []
  const erro = (mensagem: string) =>
    problemas.push({ campoId: campo.id, campoNome: campo.nome, mensagem })

  const preenchida = respostaPreenchida(campo, valor)

  if (campo.obrigatorio && !preenchida) {
    erro('Campo obrigatório.')
    return problemas
  }
  if (!preenchida || !valor) return problemas

  switch (campo.tipoCampo) {
    case TipoCampo.SELECAO_UNICA: {
      if (campo.opcoes.length > 0 && valor.texto && !campo.opcoes.includes(valor.texto)) {
        erro(`Opção inválida: "${valor.texto}".`)
      }
      break
    }
    case TipoCampo.MULTISSELECAO: {
      if (campo.opcoes.length > 0) {
        for (const op of valor.opcoes ?? []) {
          if (!campo.opcoes.includes(op)) erro(`Opção inválida: "${op}".`)
        }
      }
      break
    }
    case TipoCampo.NUMERO: {
      if (valor.numero !== null && valor.numero !== undefined && !Number.isFinite(valor.numero)) {
        erro('Número inválido.')
      }
      break
    }
    case TipoCampo.DATA: {
      if (valor.data && !dataValida(valor.data)) erro('Data inválida.')
      break
    }
    default:
      break
  }

  // Toda não conformidade sai da inspeção com dono, ação e prazo. Uma mensagem só
  // por campo, com tudo o que falta: é uma linha na lista de pendências.
  if (ehNaoConformidade(campo, valor)) {
    const faltas = faltasDaNaoConformidade(valor)
    if (faltas.length > 0) erro(`Não conforme: informe ${juntar(faltas)}.`)
    else if (!dataValida(valor.ncPrazo!)) erro('Não conforme: prazo inválido.')
  }
  return problemas
}

/// "a, b e c".
function juntar(itens: string[]): string {
  return itens.length <= 1 ? (itens[0] ?? '') : `${itens.slice(0, -1).join(', ')} e ${itens.at(-1)}`
}

/// Valida a inspeção inteira. Devolve todos os problemas de uma vez — o inspetor em
/// campo não merece descobrir um erro por vez.
export function validarInspecao(
  campos: CampoChecklist[],
  respostas: Map<string, ValorResposta>,
): Problema[] {
  return campos
    .filter((c) => c.tipoCampo !== TipoCampo.ASSINATURA || c.obrigatorio)
    .flatMap((campo) => validarResposta(campo, respostas.get(campo.id)))
}

/// `aaaa-mm-dd` que existe de verdade. Rejeita 2026-02-31.
export function dataValida(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number]
  const dt = new Date(Date.UTC(a, m - 1, d))
  return dt.getUTCFullYear() === a && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

/// Agrupa os campos por seção, preservando a ordem de cadastro. Campos sem seção caem
/// num grupo de nome vazio, que a tela renderiza sem cabeçalho.
export function agruparPorSecao(campos: CampoChecklist[]): Array<{ secao: string; campos: CampoChecklist[] }> {
  const ordenados = [...campos].sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR'))
  const grupos = new Map<string, CampoChecklist[]>()
  for (const campo of ordenados) {
    const chave = campo.secao?.trim() ?? ''
    const lista = grupos.get(chave)
    if (lista) lista.push(campo)
    else grupos.set(chave, [campo])
  }
  return [...grupos].map(([secao, campos]) => ({ secao, campos }))
}
